import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import { queryClient } from '@/hooks/QueryClient';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import { SupportedAdditionalInfoDtoIn } from '@/models/in/SupportedAdditionalInfoDtoIn';
import { ChildDtoIn } from '@/models/in/ChildDtoIn';
import { ChildDtoOut } from '@/models/out/ChildDtoOut';
import { AddChildrenDtoOut } from '@/models/out/AddChildrenDtoOut';
import { FileReaderHelper } from '@/helpers/fileReaderHelper';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import { isBirthDateInAllowedRange, MIN_PLAUSIBLE_YEAR } from '@/helpers/dateHelper';
import ExistingChildren from './ExistingChildren';
import AddChildrenForm from './AddChildrenForm';
import styles from './AddChildrenPopup.module.scss';

interface AddChildrenPopupProps {
  fundId: number;
  supportedId: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  onRefreshList?: () => Promise<any>;
}

const AddChildrenPopup = ({ fundId, supportedId, open, setOpen, onRefreshList }: AddChildrenPopupProps) => {
  const { t } = useTranslation();
  const [newChildren, setNewChildren] = useState<ChildDtoOut[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);

  const additionalInfoApiUrl = fundId && supportedId ? `funds/${fundId}/supporteds/${supportedId}/additional-info` : '';
  const { data: additionalInfo } = useGetAuthApi<SupportedAdditionalInfoDtoIn>(additionalInfoApiUrl);

  const addChildrenApiUrl = fundId && supportedId ? `funds/${fundId}/supporteds/${supportedId}/children` : '';
  const { mutateAsync: addChildren, isPending: isSubmitting } = usePostAuthApi<ChildDtoIn[], AddChildrenDtoOut>(addChildrenApiUrl);

  // Reset state when popup closes, and add one empty child when opening
  useEffect(() => {
    if (open) {
      // When opening, start with one empty child
      if (newChildren.length === 0) {
        setNewChildren([
          {
            firstName: '',
            israeliId: '',
            birthDate: undefined,
            livesWithSupported: true,
          },
        ]);
      }
    } else {
      // When closing, reset everything
      setNewChildren([]);
      setDocuments([]);
    }
  }, [open]);

  const handleSubmit = async () => {
    const errors: string[] = [];

    if (newChildren.length === 0) {
      alert(t('pleaseAddAtLeastOneChild'));
      return;
    }

    // Validate all children have required fields
    const childrenWithMissingFields = newChildren.filter((child) => !child.firstName || !child.israeliId || !child.birthDate);

    if (childrenWithMissingFields.length > 0) {
      errors.push(t('pleaseCompleteAllRequiredFields'));
    }

    // A birth date centuries off is as wrong as a missing one, and it hides itself in the
    // children table afterwards - stop it here (SPR-1179).
    newChildren
      .filter((child) => !isBirthDateInAllowedRange(child.birthDate))
      .forEach((child) => {
        errors.push(`${child.firstName || t('child')}: ${t('birthDateTooEarly', { year: MIN_PLAUSIBLE_YEAR })}`);
      });

    // Validate Israeli IDs using full validation (including check digit)
    // Only check IDs that are filled in
    const childrenWithInvalidId = newChildren.filter((child) => child.israeliId && !isValidIsraeliID(child.israeliId));

    if (childrenWithInvalidId.length > 0) {
      childrenWithInvalidId.forEach((child) => {
        errors.push(`${t('israeliId')} ${child.israeliId} ${t('invalid')}`);
      });
    }

    // Validate that at least one document is uploaded
    if (documents.length === 0) {
      errors.push(t('pleaseUploadAtLeastOneDocument'));
    }

    // Show all errors if any
    if (errors.length > 0) {
      alert(errors.join('\n'));
      return;
    }

    try {
      // Convert files to base64 using FileReaderHelper
      const convertedDocuments = await Promise.all(documents.map((file) => FileReaderHelper.fileToDocumentDtoOut(file)));
      const documentsFlat = convertedDocuments.flat();

      // Send children and documents separately
      // Backend uploads documents once and shares IDs with all children
      const requestData: AddChildrenDtoOut = {
        children: newChildren.map((child) => ({
          firstName: child.firstName,
          israeliId: child.israeliId,
          birthDate: child.birthDate,
          livesWithSupported: child.livesWithSupported,
        })),
        documents: documentsFlat,
      };

      // Call API endpoint
      await addChildren(requestData);

      // Show success message
      alert(t('childrenAddedSuccessfully'));

      // Close popup immediately
      setOpen(false);

      // Invalidate additional-info queries to refresh data in AdditionalInfo blade
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0] as string;
          return key?.includes(`supporteds/${supportedId}/additional-info`);
        },
      });

      // Refresh supporteds list
      await onRefreshList?.();
    } catch (error: any) {
      const errorMessage = error?.response?.data || error?.message || t('errorAddingChildren');
      alert(errorMessage);
    }
  };

  const isSubmitDisabled = newChildren.length === 0 || isSubmitting;

  return (
    <MainDialogLayout open={open} onOpenChange={setOpen} title={t('children')}>
      <div className={styles.wrapper}>
        <div className={styles.content}>
          {/* Existing Children Section */}
          <ExistingChildren children={additionalInfo?.children || []} />

          {/* Divider */}
          {additionalInfo?.children && additionalInfo.children.length > 0 && <div className={styles.divider} />}

          {/* Add New Children Section */}
          <AddChildrenForm children={newChildren} onChildrenChange={setNewChildren} documents={documents} onDocumentsChange={setDocuments} />
        </div>

        {/* Footer with Submit Button */}
        <div className={styles.footer}>
          <button onClick={handleSubmit} disabled={isSubmitDisabled} className={isSubmitDisabled ? styles.submitButtonDisabled : styles.submitButton}>
            {isSubmitting ? t('saving') : t('saveChildren')}
          </button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default AddChildrenPopup;
